/**
 * Permission Service Constants
 * Defines resource types and role mappings
 */

export const RESOURCE_TYPES = {
  FOLDER: 'folder',
  MEETING: 'meeting',
  ORGANIZATION: 'organization',
};

export const RESOURCE_DISPLAY_NAMES = {
  [RESOURCE_TYPES.FOLDER]: 'folder',
  [RESOURCE_TYPES.MEETING]: 'video',
  [RESOURCE_TYPES.ORGANIZATION]: 'organization',
};

export const getResourceDisplayName = (resourceType) =>
  RESOURCE_DISPLAY_NAMES[resourceType] || resourceType || '';

export const FOLDER_ROLES = {
  OWNER: 'owner',
  CONTRIBUTOR: 'contributor',
  VIEWER: 'viewer',
};

export const MEETING_ROLES = {
  CREATOR: 'creator',
  CONTRIBUTOR: 'contributor',
  CONTRIBUTOR_CLOSED: 'contributor-closed',
  REVIEWER: 'reviewer',
  VIEWER: 'viewer',
};

export const MEETING_ROLE_PRIORITY = {
  [MEETING_ROLES.CREATOR]: 5,
  [MEETING_ROLES.CONTRIBUTOR]: 4,
  [MEETING_ROLES.REVIEWER]: 3,
  [MEETING_ROLES.CONTRIBUTOR_CLOSED]: 2,
  [MEETING_ROLES.VIEWER]: 1,
};

export const ORG_ROLES = {
  OWNER: 'owner',
  MANAGER: 'manager',
  VIEWER: 'viewer',
};

// Unified ROLES export for PermissionManager
export const ROLES = {
  ...FOLDER_ROLES,
  ...MEETING_ROLES,
  ...ORG_ROLES,
};

const ROLE_HINTS = {
  [RESOURCE_TYPES.FOLDER]: {
    [FOLDER_ROLES.OWNER]:
      'Full control of the folder and all its contents',
    [FOLDER_ROLES.CONTRIBUTOR]:
      'Can view, create, and publish videos and add subfolders in this folder',
    [FOLDER_ROLES.VIEWER]:
      'Can only view files or folders that they are assigned to',
  },
  [RESOURCE_TYPES.MEETING]: {
    [MEETING_ROLES.CREATOR]: 'Full control of the video',
    [MEETING_ROLES.CONTRIBUTOR]: 'Can edit assigned sections',
    [MEETING_ROLES.CONTRIBUTOR_CLOSED]:
      'Completed their assigned sections',
    [MEETING_ROLES.REVIEWER]: 'Can review and comment on the video',
    [MEETING_ROLES.VIEWER]: 'Can only view the video',
  },
  [RESOURCE_TYPES.ORGANIZATION]: {
    [ORG_ROLES.OWNER]:
      'Full control of the organization and all its resources',
    [ORG_ROLES.VIEWER]: 'Can only view organization resources',
  },
};

/**
 * Get available roles for a resource type
 */
export const getAvailableRoles = (resourceType) => {
  switch (resourceType) {
    case RESOURCE_TYPES.FOLDER:
      return Object.values(FOLDER_ROLES);
    case RESOURCE_TYPES.MEETING:
      return Object.values(MEETING_ROLES);
    case RESOURCE_TYPES.ORGANIZATION:
      // Filter out MANAGER as it's not used
      return Object.values(ORG_ROLES).filter(
        (role) => role !== ORG_ROLES.MANAGER
      );
    default:
      return [];
  }
};

/**
 * Role display name mappings
 * Maps role values to their display names in the UI
 */
const ROLE_DISPLAY_NAMES = {
  [ORG_ROLES.OWNER]: 'Admin', // Organization owner displayed as "Admin"
};

/**
 * Get role display name
 * @param {string} role - Role value (e.g., 'owner', 'viewer', 'contributor')
 * @param {string} resourceType - Optional resource type for context-specific mappings
 * @returns {string} Display name for the role
 */
export const getRoleDisplayName = (role, resourceType = null) => {
  if (!role) return '';

  // Check if there's a specific display name mapping
  if (ROLE_DISPLAY_NAMES[role]) {
    // Only use "Admin" mapping for organization roles
    if (role === ORG_ROLES.OWNER && resourceType === RESOURCE_TYPES.ORGANIZATION) {
      return ROLE_DISPLAY_NAMES[role];
    }
    // For other resource types, use default capitalization
    if (role === ORG_ROLES.OWNER && resourceType !== RESOURCE_TYPES.ORGANIZATION) {
      // Fall through to default capitalization for folder/meeting owners
    } else {
      return ROLE_DISPLAY_NAMES[role];
    }
  }

  // Capitalize first letter and replace hyphens with spaces
  const displayName = role
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return displayName;
};

export const getRoleHint = (resourceType, role) =>
  ROLE_HINTS[resourceType]?.[role] || '';

